import { useEffect, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { MapPin, Sparkles, AlertTriangle, Train, Plane, Bus } from "lucide-react";
import { waLink } from "../lib/wa";
import { useCity } from "../context/CityContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANDMARK_ICON = { railway: Train, airport: Plane, bus: Bus };

function useLocationConfig() {
  const [cfg, setCfg] = useState({ hints: [], landmarks: {} });
  useEffect(() => {
    fetch(`${API}/config`).then((r) => r.json()).then((c) => setCfg({
      hints: c.non_serviceable_hints || [],
      landmarks: c.nearby_landmarks || {},
    })).catch(() => {});
  }, []);
  return cfg;
}

/**
 * SimpleLocationInput — plain text area + landmark. No map.
 * Also shows tap-and-fill chips for nearby landmarks (railway/airport/bus) for the active city.
 * Emits { address, landmark }.
 */
export default function SimpleLocationInput({
  label = "Pickup / area",
  landmarkLabel = "Nearby landmark",
  placeholder = "e.g. Kothrud",
  landmarkPlaceholder = "e.g. Opposite Pune Central Mall",
  value = { address: "", landmark: "" },
  onChange,
  testid = "simple-location",
  showLandmarkChips = true,
}) {
  const { hints, landmarks } = useLocationConfig();
  const { city } = useCity();
  const cityLandmarks = (city && landmarks[city.slug]) || [];

  const matchedHint = hints.find(
    (h) => value.address && value.address.toLowerCase().includes(h.toLowerCase()),
  );

  const patch = (k, v) => onChange?.({ ...value, [k]: v });
  const pickLandmark = (lm) => onChange?.({ address: lm.area, landmark: lm.name });

  return (
    <div data-testid={testid} className="space-y-3">
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">{label}</Label>
        <div className="mt-1.5 relative">
          <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            data-testid={`${testid}-area`}
            className="pl-9"
            placeholder={placeholder}
            value={value.address || ""}
            onChange={(e) => patch("address", e.target.value)}
          />
        </div>
      </div>

      {/* Nearby landmarks quick-pick (railway / airport / bus) */}
      {showLandmarkChips && cityLandmarks.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">
            Nearby stations & airports · tap to fill
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-landmarks-quickpick`}>
            {cityLandmarks.map((lm) => {
              const Ico = LANDMARK_ICON[lm.type] || MapPin;
              return (
                <button
                  type="button"
                  key={`${lm.type}-${lm.name}`}
                  onClick={() => pickLandmark(lm)}
                  data-testid={`${testid}-landmark-${lm.name.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g,'')}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white hover:border-[color:var(--secondary)] px-3 py-1.5 text-[11px] font-medium transition-colors"
                  title={`${lm.name} · ${lm.area}`}
                >
                  <Ico className="w-3 h-3" style={{ color: "var(--secondary)" }} />
                  <span className="truncate max-w-[160px]">{lm.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">{landmarkLabel}</Label>
        <div className="mt-1.5 relative">
          <Sparkles className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            data-testid={`${testid}-landmark`}
            className="pl-9"
            placeholder={landmarkPlaceholder}
            value={value.landmark || ""}
            onChange={(e) => patch("landmark", e.target.value)}
          />
        </div>
      </div>

      {matchedHint && (
        <div
          className="rounded-2xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 flex gap-2"
          data-testid={`${testid}-nonserviceable-warning`}
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <b>{matchedHint}</b> is currently outside our regular serviceable area.
            Please <a
              href={waLink(`Hi HeylpMate! I need help around ${value.address || matchedHint}. Please help.`)}
              className="underline font-bold"
              target="_blank" rel="noreferrer"
            >WhatsApp us</a> — we'll still try to arrange a Mate for you.
          </div>
        </div>
      )}
    </div>
  );
}
